import { useState, useEffect, useCallback, useRef } from "react";
import { VisitorDrawer } from "./VisitorDrawer";
import { useTranslation } from "react-i18next";
import { faPersonWalking } from "@fortawesome/pro-solid-svg-icons/faPersonWalking";
import { faWheelchair } from "@fortawesome/pro-solid-svg-icons/faWheelchair";
import { faAngleDown } from "@fortawesome/pro-solid-svg-icons/faAngleDown";
import { faChevronUp } from "@fortawesome/pro-solid-svg-icons/faChevronUp";
import { POISheetHeader } from "./poi/POISheetHeader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getTranslatedValue } from "@wondermaps/db/client";
import type { POI } from "../MapCanvas";
import { PhotoCarousel } from "./poi/PhotoCarousel";
import { HoursDisplay } from "./poi/HoursDisplay";
import { EventsDisplay } from "./poi/EventsDisplay";
import { LinkPillRow } from "./poi/LinkPillRow";
import { RichDescription } from "./poi/RichDescription";
import type { POIAccessibilityAttributes } from "@wondermaps/maps";
import type { VisitorEvent } from "../../hooks/visitor/useVisitorEvents";
import { POIRatingSection } from "./poi/POIRatingSection";
import { StatusBadge } from "./poi/StatusBadge";
import { findIconByName } from "../../data/fontawesome-icons";
import { useEngagementTimer } from "../../hooks/useAnalytics";
import { usePoiRating } from "../../hooks/visitor/usePoiRating";
import { formatWalkTime } from "../../lib/walk-time";
import { useSheetOverlay } from "../../contexts/SheetOverlayContext";

/** Snap points: half → full */
const SNAP_POINTS: (number | string)[] = [0.55, 0.92];

/** Overlay fades in from the first snap */
const FADE_FROM_INDEX = 0;

/** Resolve initial snap point for a given context */
const getSnapForContext = (
  ctx: "map" | "list" | "events" | "curated" | undefined,
) => (ctx === "list" || ctx === "curated" ? 0.92 : 0.55);

export interface POIDetailSheetProps {
  poi: POI | null;
  isOpen: boolean;
  onClose: () => void;
  context?: "map" | "list" | "events" | "curated";
  onViewOnMap?: () => void;
  hasPathNetwork?: boolean;
  onGetDirections?: () => void;
  onRequestLocation?: () => void;
  isBookmarked?: boolean;
  onBookmarkToggle?: (poiId: string) => void;
  /** Feature flag - show bookmark UI */
  bookmarksEnabled?: boolean;
  /** Events at this POI */
  events?: VisitorEvent[];
  /** Straight-line distance in meters from user to this POI */
  distance?: number;
  /** Whether accessible mode is active (affects walk time estimates) */
  accessibleMode?: boolean;
  /** Slugs needed for POI rating submission */
  orgSlug?: string;
  mapSlug?: string;
  /** Whether GPS is active (gates POI rating) */
  gpsActive?: boolean;
  /** Whether POI ratings are enabled (feature flag) */
  ratingsEnabled?: boolean;
  /** Back button handler (when opened from stack) */
  onBack?: () => void;
  /** Accent color — reserved for future brand color theming across pills/CTA */
  brandAccent?: string | null;
  /** Tap handler for "See all events at {poi}" inside the events block */
  onSeeAllEvents?: (poiId: string) => void;
}

export function POIDetailSheet({
  poi,
  isOpen,
  onClose,
  context,
  onViewOnMap,
  hasPathNetwork,
  onGetDirections,
  onRequestLocation,
  isBookmarked,
  onBookmarkToggle,
  bookmarksEnabled,
  events,
  distance,
  accessibleMode,
  orgSlug,
  mapSlug,
  gpsActive,
  ratingsEnabled,
  onBack,
  brandAccent,
  onSeeAllEvents,
}: POIDetailSheetProps) {
  const { t, i18n } = useTranslation("pois");
  const lang = i18n.language;
  const translatedName = poi
    ? getTranslatedValue(poi.name, poi.nameTranslations, lang)
    : "";

  const { setSheet, clearSheet } = useSheetOverlay();

  // Track lightbox state to block drawer dismiss while lightbox is open
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  // A11y section toggle
  const [showA11y, setShowA11y] = useState(false);

  // Snap point state — initial depends on context
  const [activeSnap, setActiveSnap] = useState<number | string | null>(
    getSnapForContext(context),
  );

  // Reset snap when POI changes
  const poiId = poi?.id;
  useEffect(() => {
    if (poiId) {
      setActiveSnap(getSnapForContext(context));
      setShowA11y(false);
    }
  }, [poiId, context]);

  // Sync overlay context with snap changes + clean up on close
  useEffect(() => {
    if (isOpen && poi) {
      setSheet("poi", activeSnap);
    } else if (!isOpen) {
      clearSheet();
    }
  }, [isOpen, poi, activeSnap, setSheet, clearSheet]);

  const handleSnapChange = useCallback((point: number | string | null) => {
    setActiveSnap(point);
  }, []);

  const handleClose = useCallback(() => {
    clearSheet();
    onClose();
  }, [clearSheet, onClose]);

  // Vaul snap-drawer scroll/dismiss arbitration (pointer-event driven):
  //
  // Vaul's shouldDrag short-circuits to "drag" when drawer translateY > 0 (any
  // non-top snap), bypassing its scroll-parent walk. Setting data-vaul-no-drag
  // on the scroll container makes Vaul bail earlier (closest() match), but a
  // naive scroll-event listener flickers during iOS bounce/momentum and also
  // breaks snap-up gestures (blocks ALL drag, not just dismiss).
  //
  // Solution: snapshot scrollTop at pointerdown, then on the first move past a
  // deadzone, set data-vaul-no-drag only if gesture is downward AND we started
  // scrolled. Upward gestures (snap-up) always pass through. Cleared on
  // pointerup so each gesture re-evaluates from scratch.
  //
  // See docs/plans/116-poi-bottom-sheet-ux-fixes.md + docs/learnings.md.
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !isOpen) return;

    let startY = 0;
    let gestureScrolled = false;
    let directionLocked = false;
    const DEADZONE_PX = 5;

    const onPointerDown = (e: PointerEvent) => {
      startY = e.clientY;
      gestureScrolled = el.scrollTop > 0;
      directionLocked = false;
      el.removeAttribute("data-vaul-no-drag");
    };

    const onPointerMove = (e: PointerEvent) => {
      if (directionLocked) return;
      const deltaY = e.clientY - startY;
      if (Math.abs(deltaY) < DEADZONE_PX) return;
      // Block drag only when scrolled AND swiping down (would dismiss/snap-down).
      // Upward swipes (snap-up or content scroll-down) leave attribute off.
      if (gestureScrolled && deltaY > 0) {
        el.setAttribute("data-vaul-no-drag", "");
      }
      directionLocked = true;
    };

    const onPointerEnd = () => {
      el.removeAttribute("data-vaul-no-drag");
    };

    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerup", onPointerEnd);
    el.addEventListener("pointercancel", onPointerEnd);

    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", onPointerEnd);
      el.removeEventListener("pointercancel", onPointerEnd);
    };
  }, [isOpen, poiId]);

  // Determine full-expand visibility
  const isFull = activeSnap === 0.92;

  // POIs with photos use progressive disclosure (0.55 → 0.92).
  // Lean POIs (no photos) show everything at 0.55 to avoid blank white space.
  const hasRichHalfContent = !!(poi?.photos?.length || (events && events.length > 0));
  const showAllContent = isFull || !hasRichHalfContent;

  // Track engagement (fires after 5s of viewing)
  useEngagementTimer(poi ? { id: poi.id, name: poi.name } : null, isOpen);

  // POI rating hook — gated by org-level flag + per-POI toggle
  const poiRating = usePoiRating({
    poiId: poi?.id,
    poiStatus: poi?.status,
    orgSlug: orgSlug ?? "",
    mapSlug: mapSlug ?? "",
    gpsActive: !!gpsActive,
    isOpen,
  });

  // Walk time
  const walkTime =
    distance !== undefined ? formatWalkTime(distance, accessibleMode, t) : null;

  // Accessibility data
  const accessibility = (
    poi?.propertiesJson as Record<string, unknown> | null
  )?.accessibility as POIAccessibilityAttributes | undefined;
  const hasA11y =
    accessibility &&
    Object.entries(accessibility).some(([key, value]) => {
      if (key === "dietary_options")
        return Array.isArray(value) && value.length > 0;
      return value === true;
    });

  return (
    <VisitorDrawer
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      snapPoints={SNAP_POINTS}
      activeSnapPoint={activeSnap}
      onSnapPointChange={handleSnapChange}
      fadeFromIndex={FADE_FROM_INDEX}
      title={translatedName || "POI Details"}
      dismissible={!isLightboxOpen}
    >
      {poi && (
        <div
          ref={scrollRef}
          className="overflow-y-auto flex-1 min-h-0"
          style={{
            // Vaul translates the drawer down by (1 - snap) * 100vh, pushing
            // its bottom below the viewport. Cap visible scroll area so the
            // last content (rating card + safe-area spacer) lands inside the
            // viewport instead of off-screen. dvh excludes mobile browser
            // chrome. -24px accounts for the drag handle row.
            maxHeight: `calc(${
              typeof activeSnap === "number" ? activeSnap * 100 : 92
            }dvh - 24px)`,
          }}
        >
          <div className="px-4 pt-4 space-y-3">
            {/* === PEEK CONTENT: always visible === */}

            {/* A. Title + [map] + heart + X + subtitle */}
            <POISheetHeader
              title={translatedName}
              subtitle={[
                ...(poi.category?.name ? (() => {
                  const catIcon = poi.category!.icon ? findIconByName(poi.category!.icon) : null;
                  return [{ icon: (catIcon?.icon ?? faPersonWalking), text: getTranslatedValue(poi.category!.name, poi.category!.nameTranslations, lang) }];
                })() : []),
                ...(walkTime ? [{ icon: accessibleMode ? faWheelchair : faPersonWalking, text: walkTime }] : []),
              ]}
              isBookmarked={isBookmarked}
              onBookmarkToggle={bookmarksEnabled && onBookmarkToggle ? () => onBookmarkToggle(poi.id) : undefined}
              bookmarksEnabled={bookmarksEnabled}
              onClose={handleClose}
              onBack={onBack}
              onViewOnMap={context === "list" || context === "events" ? onViewOnMap : undefined}
            />

            {/* B. Sponsor badge */}
            {(poi.sponsorRef || poi.sponsorTier) && (
              <span
                className={`inline-block px-2 py-0.5 text-sm font-medium rounded-sm${!brandAccent ? " text-teal-600 bg-teal-600/[0.08]" : ""}`}
                style={brandAccent ? { color: brandAccent, backgroundColor: `${brandAccent}14` } : undefined}
              >
                {poi.sponsorRef?.sponsorName
                  ? t("sponsoredBy", { name: poi.sponsorRef.sponsorName })
                  : t("sponsor")}
              </span>
            )}

            {/* C. Status badge (temp_closed / seasonal) */}
            {poi.status && poi.status !== "normal" && (
              <StatusBadge
                status={poi.status}
                statusNote={poi.statusNote ?? null}
                hours={poi.hours}
                seasonDates={poi.seasonDates}
              />
            )}

            {/* C2. Hours row */}
            {poi.hours && <HoursDisplay hours={poi.hours} />}

            {/* D. Link pills */}
            <LinkPillRow
              links={poi.links ?? []}
              onGetDirections={onGetDirections}
              onRequestLocation={onRequestLocation}
              hasPathNetwork={hasPathNetwork}
              gpsActive={gpsActive}
              brandAccent={brandAccent}
            />

            {/* E. Photo carousel */}
            {poi.photos && poi.photos.length > 0 && (
              <div className="animate-in fade-in duration-300">
                <PhotoCarousel
                  photos={poi.photos}
                  onLightboxChange={setIsLightboxOpen}
                />
              </div>
            )}

            {/* F. Description — RichDescription handles its own line-clamp + read-more.
                When user expands a clamped description while at half-snap,
                grow the sheet to full so the expanded text isn't cut off. */}
            <RichDescription
              content={poi.descriptionRich}
              fallbackText={poi.description}
              accentColor={brandAccent ?? undefined}
              onExpand={() => {
                if (!isFull) setActiveSnap(0.92);
              }}
            />

            {/* Events */}
            {events && events.length > 0 && (
              <EventsDisplay
                events={events}
                compact
                brandAccent={brandAccent}
                poiName={translatedName}
                onSeeAllEvents={
                  onSeeAllEvents ? () => onSeeAllEvents(poi.id) : undefined
                }
              />
            )}

            {/* === FULL / ALL CONTENT === */}
            {showAllContent && (
              <>
                {/* G. Tags row + a11y trigger */}
                {(poi.tags?.length || hasA11y) && (
                  <div className="flex flex-wrap items-center gap-1.5">
                    {poi.tags?.map((tag) => (
                      <span
                        key={tag.slug}
                        className="px-2 py-0.5 rounded-lg bg-secondary text-sm font-medium"
                      >
                        {tag.name}
                      </span>
                    ))}
                    {hasA11y && (
                      <button
                        type="button"
                        onClick={() => setShowA11y(!showA11y)}
                        className="p-2 -m-2 ml-auto inline-flex items-center gap-1"
                        aria-expanded={showA11y}
                        aria-label={t("accessibilityFeatures")}
                      >
                        <FontAwesomeIcon
                          icon={faWheelchair}
                          className="h-3.5 w-3.5 text-muted-foreground"
                        />
                        <FontAwesomeIcon
                          icon={faAngleDown}
                          className={`h-3 w-3 text-muted-foreground transition-transform ${showA11y ? "rotate-180" : ""}`}
                        />
                      </button>
                    )}
                  </div>
                )}

                {/* H. Accessibility card — title + pills */}
                {showA11y && hasA11y && (
                  <div className="bg-foreground/[0.04] rounded-2xl p-3 space-y-2.5">
                    <h3 className="text-xs font-medium tracking-widest text-muted-foreground uppercase">
                      {t("accessibilityFeatures")}
                    </h3>
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(
                        accessibility as Record<string, unknown>
                      )
                        .filter(([key, val]) => key !== "dietary_options" && val === true)
                        .map(([key]) => (
                          <span
                            key={key}
                            className="px-3 py-1 rounded-full bg-secondary text-sm font-medium"
                          >
                            {t(`accessibility.${key}`, { defaultValue: key })}
                          </span>
                        ))}
                    </div>
                  </div>
                )}

                {/* I. Rating card */}
                {ratingsEnabled &&
                  orgSlug &&
                  mapSlug &&
                  poi.ratingsEnabled !== false && (
                    <POIRatingSection
                      {...poiRating}
                      onRequestLocation={
                        !gpsActive ? onRequestLocation : undefined
                      }
                    />
                  )}
              </>
            )}

            {/* Swipe hint — only for rich POIs with more to reveal; prototype for designer review */}
            {activeSnap === 0.55 && hasRichHalfContent && (
              <div className="sticky bottom-0 flex flex-col items-center pb-3 pt-8 bg-gradient-to-t from-background to-transparent pointer-events-none">
                <FontAwesomeIcon
                  icon={faChevronUp}
                  className="h-3.5 w-3.5 text-muted-foreground"
                />
                <span className="text-xs text-muted-foreground mt-1">
                  {t("swipeForMore")}
                </span>
              </div>
            )}
          </div>
          {/* Bottom breathing room. The scroll container's max-height already
              clips to the visible drawer (Vaul snap-translate fix), so this
              spacer adds visual padding below the last card at scroll-end
              plus the iOS home-indicator inset. Outside the space-y-3
              container so it doesn't pick up extra top margin. */}
          <div
            aria-hidden="true"
            style={{ height: "calc(2rem + env(safe-area-inset-bottom))" }}
          />
        </div>
      )}
    </VisitorDrawer>
  );
}
